import { StearsBusinessDateRangeMock } from "@/src/__mocks__/StearsBusinessDateRangeMock"
import CustomCalendar from "@/src/common/components/CustomCalendar/CustomCalendar"
import { CustomDateRangeProps } from "@/src/common/types"
import Card from "@/src/sb/components/FMDQTable/Card"
import DynamicSelect from "@/src/sb/components/FMDQTable/DynamicSelect"
import { format } from "date-fns"
import React, { useState } from "react"

const CustomDateRange: React.FC<CustomDateRangeProps> = ({
  showCard,
  handleCardDisplay,
  handleApplyDateRange,
  handleCloseDateRange,
  handleResetDate
}) => {
  const [selectedOption, setSelectedOption] = useState<any>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [showDateDisplay, setShowDateDisplay] = useState(false)

  const handleOptionChange = (option: string) => {
    setSelectedOption(option)
  }

  const handleDateChange = (date: Date | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date)
    } else {
      setEndDate(date)
    }
  }

  const handleApply = () => {
    handleApplyDateRange(selectedOption, startDate, endDate)
    setShowDateDisplay(true)
    handleCardDisplay()
  }

  const handleResetDateRange = () => {
    setStartDate(null)
    setEndDate(null)
    setSelectedOption(null)
    setShowDateDisplay(false)
    handleResetDate()
  }

  const getDateDisplay = (label: string, dateRange?: any) => (
    <>
      <span className="text-[#9CA3AF] text-xs font-normal">Date: </span>
      <span className="text-white text-xs font-normal">{label}</span>
      {dateRange && (
        <>
          <span>{dateRange.start}</span>
          {dateRange.separator && <span>{dateRange.separator}</span>}
          <span>{dateRange.end}</span>
        </>
      )}
    </>
  )

  const formattedStartDate = startDate ? format(startDate, "MMM d, yyyy") : null
  const formattedEndDate = endDate ? format(endDate, "MMM d, yyyy") : null

  let dateDisplay

  switch (selectedOption) {
    case "Between":
      dateDisplay = formattedEndDate
        ? getDateDisplay("Between", {
            start: formattedStartDate,
            end: formattedEndDate,
            separator: "-"
          })
        : "Select Date Range"
      break

    case "Before":
      dateDisplay = formattedStartDate
        ? getDateDisplay("Before", { start: formattedStartDate })
        : "Select Date"
      break

    case "On":
      dateDisplay = formattedStartDate
        ? getDateDisplay("On", { start: formattedStartDate })
        : "Select Date"
      break

    case "After":
      dateDisplay = formattedStartDate
        ? getDateDisplay("After", { start: formattedStartDate })
        : "Select Date"
      break

    case "Last 5 days":
      dateDisplay = getDateDisplay("Last 5 days")
      break

    case "1month":
      dateDisplay = getDateDisplay("1 month")
      break

    case "YTD":
      dateDisplay = getDateDisplay("Year to Date (YTD)")
      break

    case "6months":
      dateDisplay = getDateDisplay("6 months")
      break

    default:
      dateDisplay = "Select Date"
      break
  }

  return (
    <div className="relative">
      <DynamicSelect
        placeholder={showDateDisplay ? dateDisplay : "Select Date"}
        onClick={handleCardDisplay}
        theme={showDateDisplay ? "custom" : "default"}
      />
      {showCard && (
        <Card
          showCard={showCard}
          className="absolute"
          onApply={handleApply}
          onClose={handleCloseDateRange}
        >
          <section className="flex items-center justify-between py-2 px-5 text-xs font-normal border-b-[0.5px] dark:border-[#4B5563]">
            <span className="text-white">Select Date</span>
            <span
              className="underline text-[#9CA3AF] cursor-pointer"
              onClick={handleResetDateRange}
            >
              Reset
            </span>
          </section>
          <section className="px-5 py-2.5">
            <DynamicSelect
              placeholder={selectedOption || "Select"}
              options={StearsBusinessDateRangeMock}
              theme="custom"
              onChange={handleOptionChange}
            />
            {selectedOption === "Between" && (
              <div className="flex items-center gap-2 py-0 max-w-full mt-2">
                <CustomCalendar
                  handleChange={(date) => handleDateChange(date, "start")}
                  selectedDate={startDate}
                />
                <span className="top-1 w-2 border-l border-[#4B5563] border-[0.5px]" />
                <CustomCalendar
                  handleChange={(date) => handleDateChange(date, "end")}
                  selectedDate={endDate}
                />
              </div>
            )}
            {["Before", "On", "After"].includes(selectedOption) && (
              <CustomCalendar
                handleChange={(date) => handleDateChange(date, "start")}
                selectedDate={startDate}
                className="mt-2"
              />
            )}
          </section>
        </Card>
      )}
    </div>
  )
}

export default CustomDateRange
